/**
 * Receipt builder — converts bill/KOT data into ESC/POS byte commands.
 *
 * Mirrors the layout from services/api/src/billing/print.service.ts
 * but outputs ESC/POS bytes instead of PDF.
 */
import { EscPosEncoder, toPrintableAscii } from './encoder';
import { formatCurrency } from '@/lib/format';
import type { CurrencyCode } from '@bbpos/types';
import type {
  ReceiptBillData,
  ReceiptTenantData,
  ReceiptLocationData,
  ReceiptOptions,
  ReceiptTaxPresentation,
  KotData,
  PaperWidth,
} from './types';
import { resolveTaxRegime } from '@bbpos/ui';
import { getLocationTimezone } from '@/lib/tenant-clock';
import { NOT_GST_REGISTERED } from '@/lib/gst';

// ─── Date formatting (the store's clock, same as backend print.service.ts) ──

/** On the till's store clock (T28) — its own zone, else the tenant's. */
function formatStoreDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: getLocationTimezone(),
  });
}

/** Truncate a string to fit within maxLen, appending ".." if truncated. */
function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen - 2) + '..';
}

/**
 * India GST: the tax lines per rate in the bill's saved tax type (gst-parked
 * 080, D8) — `CGST 9%` + `SGST 9%`, or `IGST 18%`. The split is the server's:
 * per line SGST = floor(tax / 2), CGST takes the odd paisa. Empty when no line
 * is taxed or the bill carries no GST (NONE).
 */
function gstSplitLines(bill: ReceiptBillData): Array<{ label: string; amount: number }> {
  if (bill.supply_type === 'NONE') return [];
  const inter = bill.supply_type === 'INTER';
  const byRate = new Map<number, { tax: number; cgst: number; sgst: number }>();
  for (const item of bill.items) {
    const rate = Number(item.gst_rate);
    if (!(rate > 0)) continue;
    const lineTax = Number(item.line_tax);
    const acc = byRate.get(rate) ?? { tax: 0, cgst: 0, sgst: 0 };
    const sgst = Math.floor(lineTax / 2);
    acc.tax += lineTax;
    acc.sgst += sgst;
    acc.cgst += lineTax - sgst;
    byRate.set(rate, acc);
  }
  return [...byRate.entries()]
    .sort(([a], [b]) => a - b)
    .flatMap(([rate, { tax, cgst, sgst }]) =>
      inter
        ? [{ label: `IGST ${rate}%`, amount: tax }]
        : [
            { label: `CGST ${rate / 2}%`, amount: cgst },
            { label: `SGST ${rate / 2}%`, amount: sgst },
          ],
    );
}

// ─── Bill receipt ───────────────────────────────────────────────────────────

/**
 * `currency` is the tenant's immutable currency (one per tenant, no FX).
 * It defaults to `'INR'`, so existing call sites that omit it produce a
 * byte-identical INR receipt. Money is formatted via the shared,
 * subunit-aware formatter from `@/lib/format`.
 *
 * `tax` is the regime-aware vocabulary used for labels (GST/VAT, GSTIN/TRN,
 * "Prices include applicable …" footer). The ESC/POS builder has no React
 * context, so the caller — which DOES have `useTaxRegime()` — must hand the
 * resolved labels in via the print payload. Omitting it defaults to the India
 * regime, keeping every legacy INR receipt byte-identical to today.
 */
export function buildReceipt(
  bill: ReceiptBillData,
  tenant: ReceiptTenantData,
  location: ReceiptLocationData,
  options: ReceiptOptions,
  currency: CurrencyCode = 'INR',
  tax: ReceiptTaxPresentation = resolveTaxRegime(currency),
): Uint8Array {
  return buildReceiptInternal(bill, tenant, location, options, false, currency, tax);
}

export function buildDuplicateReceipt(
  bill: ReceiptBillData,
  tenant: ReceiptTenantData,
  location: ReceiptLocationData,
  options: ReceiptOptions,
  currency: CurrencyCode = 'INR',
  tax: ReceiptTaxPresentation = resolveTaxRegime(currency),
): Uint8Array {
  return buildReceiptInternal(bill, tenant, location, options, true, currency, tax);
}

function buildReceiptInternal(
  bill: ReceiptBillData,
  tenant: ReceiptTenantData,
  location: ReceiptLocationData,
  options: ReceiptOptions,
  isDuplicate: boolean,
  currency: CurrencyCode,
  tax: ReceiptTaxPresentation,
): Uint8Array {
  const enc = EscPosEncoder.forPaper(options.paperWidth);
  const w = enc.charsPerLine;

  // ── Duplicate watermark
  if (isDuplicate) {
    enc.center().bold().size('double-both').line('*** DUPLICATE ***').normal().bold(false).newline();
  }

  // ── Header: business name (bold, centered, double-height)
  // gst-parked 090 (D5): India bill stamped NONE — the business was not GST
  // registered: no GSTIN, no tax line, no "prices include GST".
  const noGst = !!tax.splitLabels && bill.supply_type === 'NONE';

  enc.center().bold().size('double-height');
  enc.line(truncate(tenant.business_name, w));
  enc.normal().bold(false);

  // Location details
  const locParts = [location.name, location.city, location.state].filter(Boolean);
  if (locParts.length) enc.line(locParts.join(', '));
  const addrParts = [location.address, location.pincode].filter(Boolean);
  if (addrParts.length) enc.line(addrParts.join(' '));
  if (location.phone) enc.line(`Ph: ${location.phone}`);
  // Tax-id line: regime-driven label (GSTIN/TRN/VATIN/...). Suppressed when
  // the regime has no statutory id (Kuwait/Qatar/NO_VAT) or the tenant hasn't
  // configured one. For INR the label is "GSTIN", so output is byte-identical.
  if (noGst) enc.line(NOT_GST_REGISTERED);
  else if (tenant.gstin && tax.taxIdLabel) enc.line(`${tax.taxIdLabel}: ${tenant.gstin}`);

  enc.left().separator();

  // ── Bill meta (T04: a return bill prints as a credit note)
  const isCreditNote = !!bill.return_against;
  enc.center().bold().line(isCreditNote ? 'CREDIT NOTE' : 'RECEIPT').bold(false);
  enc.left();
  enc.twoColumn(isCreditNote ? 'Credit Note No' : 'Bill No', bill.bill_number);
  // T19: a reprint of a bill sold offline names the number on the first receipt.
  if (bill.offline_ref) enc.twoColumn('Offline ref', bill.offline_ref);
  enc.twoColumn('Date', formatStoreDate(bill.created_at));
  if (isCreditNote && bill.original_bill_number) {
    enc.twoColumn('Against Bill', bill.original_bill_number);
  }
  // Customer block — always printed. Walk-in bills (no customer_id) get an
  // explicit "Walk-in" line so the absence of a customer is visible on the
  // physical receipt rather than silently omitted.
  if (bill.customer_id) {
    if (bill.customer_name) enc.twoColumn('Customer', truncate(bill.customer_name, w - 10));
    if (bill.customer_phone) enc.twoColumn('Phone', bill.customer_phone);
    const earned = Number(bill.customer_loyalty_earned ?? 0);
    const used = Number(bill.customer_loyalty_used ?? 0);
    const balance = Number(bill.customer_loyalty_balance ?? 0);
    if (earned > 0 || used > 0 || balance > 0) {
      const parts: string[] = [];
      if (earned > 0) parts.push(`+${earned} earn`);
      if (used > 0) parts.push(`-${used} used`);
      parts.push(`bal ${balance}`);
      enc.twoColumn('Loyalty', truncate(parts.join(' '), w - 10));
    }
  } else {
    enc.twoColumn('Customer', 'Walk-in');
  }

  enc.separator();

  // ── Items header
  const nameW = options.paperWidth === 58 ? 14 : 22;
  enc.bold();
  enc.threeColumn(
    'Item'.padEnd(nameW),
    'Qty',
    'Amt',
  );
  enc.bold(false).separator();

  // ── Items
  for (const item of bill.items) {
    const name = truncate(item.product_name, nameW);
    const qty = String(item.quantity);
    const amt = formatCurrency(item.line_total, currency);
    enc.threeColumn(name.padEnd(nameW), qty, amt);

    // GST sub-line (smaller — just indented text)
    if (item.gst_rate > 0) {
      const gstLine = `  ${formatCurrency(item.unit_price, currency)} x${item.quantity}  ${tax.taxName} ${item.gst_rate}%`;
      enc.line(truncate(gstLine, w));
    }
  }

  enc.separator();

  // ── Totals
  enc.twoColumn('Subtotal', formatCurrency(bill.subtotal, currency));
  if (bill.discount_total > 0) {
    enc.twoColumn('Discount', `-${formatCurrency(bill.discount_total, currency)}`);
  }
  // India: CGST / SGST or IGST per rate, as the bill was saved (D8). VAT and
  // an untaxed bill keep the single tax line.
  const splitLines = tax.splitLabels ? gstSplitLines(bill) : [];
  if (noGst) {
    // No GST charged — nothing to print between subtotal and total.
  } else if (splitLines.length > 0) {
    for (const l of splitLines) enc.twoColumn(l.label, formatCurrency(l.amount, currency));
  } else {
    enc.twoColumn(tax.taxName, formatCurrency(bill.tax_total, currency));
  }
  // Cash rounding is its own line; the total below is what was charged.
  const rounding = Number(bill.rounding_adjustment ?? 0);
  if (rounding !== 0) {
    enc.twoColumn('Rounding', `${rounding < 0 ? '-' : '+'}${formatCurrency(Math.abs(rounding), currency)}`);
  }

  enc.separator();

  // ── Grand total (bold, double-size)
  enc.bold().size('double-both').center();
  enc.line(formatCurrency(bill.grand_total + rounding, currency));
  enc.normal().bold(false).left();

  // ── Payment info
  if (bill.payment_method) {
    enc.separator();
    if (isCreditNote) {
      enc.twoColumn(`Refunded (${bill.payment_method})`, formatCurrency(Math.abs(bill.amount_paid), currency));
    } else if (bill.payments && bill.payments.length > 1) {
      // A split or an exchange credit: one line per tender (PS1-001).
      for (const p of bill.payments) {
        enc.twoColumn(`Paid (${p.payment_method})`, formatCurrency(p.amount, currency));
      }
    } else {
      enc.twoColumn(`Paid (${bill.payment_method})`, formatCurrency(bill.amount_paid, currency));
    }
    if (bill.change_due > 0) {
      enc.twoColumn('Change Due', formatCurrency(bill.change_due, currency));
    }
  }

  enc.separator();

  // ── Credit note (T04): the store-credit code with a QR, which the redeem
  // field accepts when scanned; with no code (money paid back), a QR to the
  // page that verifies the credit note. Replaces the receipt QR. Since
  // CU2-007 the store-credit QR is the verify link too, ending in `#<code>`
  // for the redeem field (the bare code when there is no link).
  if (isCreditNote && bill.redemption_code) {
    enc.center().bold().line('REDEMPTION CODE');
    enc.size('double-both').line(bill.redemption_code).normal().bold(false);
    enc.qrCode(
      bill.credit_note_verify_url
        ? `${bill.credit_note_verify_url}#${bill.redemption_code}`
        : bill.redemption_code,
    );
    enc.newline();
    enc.line('Quote at counter to redeem.');
    enc.line('Anyone holding this slip can redeem.');
    enc.left().separator();
  } else if (isCreditNote && bill.credit_note_verify_url) {
    enc.center();
    enc.qrCode(bill.credit_note_verify_url);
    enc.newline();
    enc.line('Scan to verify this credit note');
    enc.left().separator();
  } else if (options.printQr && options.qrUrl) {
    // ── QR Code
    enc.center();
    enc.qrCode(options.qrUrl);
    enc.newline();
  }

  // ── Footer
  enc.center();
  if (tenant.receipt_footer) {
    enc.line(tenant.receipt_footer);
  } else {
    enc.line('Thank you for your purchase!');
  }
  if (!noGst) enc.line(`Prices include applicable ${tax.taxName}.`);

  // ── Cut
  enc.cut();

  return enc.encode();
}

// ─── KOT (Kitchen Order Ticket) ─────────────────────────────────────────────

export function buildKot(
  kot: KotData,
  paperWidth: PaperWidth = 80,
): Uint8Array {
  const enc = EscPosEncoder.forPaper(paperWidth);
  const w = enc.charsPerLine;

  // Reprint watermark
  if (kot.is_reprint) {
    enc.center().bold().size('double-both').line('*** REPRINT ***').normal().bold(false).newline();
  }

  // Header
  enc.center().bold().size('double-height');
  enc.line('** KITCHEN ORDER TICKET **');
  enc.normal().bold(false);

  enc.left().separator();

  // Order meta
  enc.twoColumn('Order', kot.order_number);
  enc.twoColumn('Type', kot.order_type.replace('_', ' '));
  if (kot.table_number) enc.twoColumn('Table', kot.table_number);
  enc.twoColumn('Time', formatStoreDate(kot.created_at));
  enc.twoColumn('Station', kot.station);

  if (kot.is_rush) {
    enc.newline().center().bold().size('double-both').line('!!! RUSH !!!').normal().bold(false).left();
  }

  enc.separator();

  // Items
  enc.bold().line('Item'.padEnd(w - 5) + 'Qty').bold(false);
  enc.separator();

  for (const item of kot.items) {
    const name = truncate(item.name, w - 6);
    const qty = String(item.quantity).padStart(3);
    enc.line(name.padEnd(w - 4) + qty);

    if (item.modifiers?.length) {
      for (const mod of item.modifiers) {
        enc.line(`  + ${truncate(mod, w - 4)}`);
      }
    }
    if (item.special_instructions) {
      enc.line(`  * ${truncate(item.special_instructions, w - 4)}`);
    }
  }

  enc.separator();
  enc.center().line(`Station: ${kot.station}`);

  enc.cut();
  return enc.encode();
}

// ─── Test receipt ───────────────────────────────────────────────────────────

export function buildTestReceipt(
  paperWidth: PaperWidth = 80,
  currency: CurrencyCode = 'INR',
): Uint8Array {
  const enc = EscPosEncoder.forPaper(paperWidth);
  // Sample amounts via the shared formatter (no hardcoded "Rs." literals).
  // `toPrintableAscii` is applied here so the string length used for column
  // padding matches the bytes the encoder will print — for INR this yields
  // exactly "Rs.100.00" etc., keeping the test print byte-identical.
  const sample = (paise: number): string =>
    toPrintableAscii(formatCurrency(paise, currency));

  enc.center().bold().size('double-height').line('Orio360 Test Print').normal().bold(false);
  enc.newline();
  enc.center().line(`Paper: ${paperWidth}mm (${enc.charsPerLine} chars)`);
  enc.center().line(new Date().toLocaleString('en-IN', { timeZone: getLocationTimezone() }));
  enc.newline();

  enc.left().separator('=');
  enc.line('Alignment Test:');
  enc.left().line('Left aligned');
  enc.center().line('Center aligned');
  enc.right().line('Right aligned');
  enc.left().separator('=');

  enc.line('Style Test:');
  enc.bold().line('Bold text').bold(false);
  enc.underline().line('Underlined text').underline(false);
  enc.size('double-width').line('Double width').normal();
  enc.size('double-height').line('Double height').normal();
  enc.separator('=');

  enc.line('Two-column test:');
  enc.twoColumn('Item A', sample(10000));
  enc.twoColumn('Item B', sample(250050));
  enc.twoColumn('TOTAL', sample(260050));
  enc.separator('=');

  enc.center().line('Printer is working correctly!');
  enc.newline();
  enc.center().line('Orio360 POS');

  enc.cut();
  return enc.encode();
}
